import type { FunctionComponent } from 'react';

import type { TransactionMetricsComponent } from '@ballerine/business-analysis-schemas';

type PrimaryMarket = {
  country: string;
  percentage: number;
};

type StatWidgetsProps = {
  metrics: NonNullable<TransactionMetricsComponent['summaryMetrics']>;
  processingMccNumber: string;
  processingMccLabel: string;
  avgTransactionAmount: number;
  avgTransactionCurrency: string;
  primaryMarkets: PrimaryMarket[];
};

type StatCardProps = {
  heading: string;
  label: string;
  value: number | string;
  tagColor: string;
};

const StatCard: FunctionComponent<StatCardProps> = ({ heading, label, value, tagColor }) => {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-2">
        <span
          className={`inline-block w-fit rounded-sm border-0 px-1.5 py-0.5 text-sm font-normal ${tagColor}`}
        >
          {heading}
        </span>
        <div className="text-2xl font-bold text-black">{value}</div>
        <div className="text-sm font-medium text-gray-600">{label}</div>
      </div>
    </div>
  );
};

const formatCurrency = (amount: number, currency: string) => {
  if (!currency || currency === 'unknown') {
    return amount === 0 ? 'N/A' : `$${amount.toFixed(2)}`;
  }

  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

export const StatWidgets: FunctionComponent<StatWidgetsProps> = ({
  metrics,
  processingMccNumber,
  processingMccLabel,
  avgTransactionAmount,
  avgTransactionCurrency,
  primaryMarkets,
}) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <StatCard
          heading="RISK SIGNALS"
          label="Indicators Detected"
          value={metrics.critical_indicators}
          tagColor="bg-red-100 text-red-700"
        />
        <StatCard
          heading="TRANSACTION PATTERNS"
          label="Requiring review"
          value={metrics.patterns_requiring_review}
          tagColor="bg-red-100 text-red-700"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-2">
            <span className="inline-block rounded-md bg-[#F4F6FD] px-2 py-1 text-sm font-bold uppercase text-[#97A3B6]">
              Processing MCC
            </span>
          </div>
          <div className="space-y-1">
            <div className="text-xl font-bold text-gray-900">
              {processingMccNumber === 'unknown' ? 'N/A' : processingMccNumber}
            </div>
            <p className="text-sm text-gray-600">
              {processingMccLabel === 'unknown' ? 'Not specified' : processingMccLabel}
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-2">
            <span className="inline-block rounded-md bg-[#F4F6FD] px-2 py-1 text-sm font-bold uppercase text-[#97A3B6]">
              Avg Transaction
            </span>
          </div>
          <div className="space-y-1">
            <div className="text-xl font-bold text-gray-900">
              {formatCurrency(avgTransactionAmount, avgTransactionCurrency)}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-2">
            <span className="inline-block rounded-md bg-[#F4F6FD] px-2 py-1 text-sm font-bold uppercase text-[#97A3B6]">
              Transactions Geos
            </span>
          </div>
          <div className="space-y-1">
            {primaryMarkets.length > 0 ? (
              primaryMarkets.map(({ country, percentage }, index) => (
                <div
                  key={country}
                  className={`flex items-center justify-between gap-2 ${
                    index === 0 ? 'text-lg font-bold text-gray-900' : 'text-sm text-gray-600'
                  }`}
                >
                  <span className="min-w-0 truncate">{country}</span>
                  <span className="shrink-0 tabular-nums">{percentage.toFixed(2)}%</span>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500">No data available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
